/**
 * The machine capacity limit contract (#483).
 *
 * Brunel already held a computer back when its newest reading said less of it was
 * free than the owner asked to keep. What it had no words for was the owner's own
 * intent: "keep more of this machine for me" or "use it harder". This module is
 * that vocabulary, and nothing more - four named presets over the real numeric
 * limits that already exist on `brunel.nodes`, plus the two new ceilings.
 *
 * Three rules hold everywhere in here:
 *
 *   * a preset is only ever a set of numbers. Picking one writes those numbers
 *     and nothing else; the owner can always see them before saving;
 *   * which preset a machine "is on" is *derived* from the numbers it actually
 *     carries, never stored beside them. Nothing can drift, and a Custom machine
 *     can never be silently rewritten by a preset it happens to be named after;
 *   * `null` on a ceiling means the guard is off, not zero. A dimension a computer
 *     cannot measure, and a ceiling the owner never set, both stay absent.
 *
 * `balanced` is deliberately the behaviour Brunel had before this change: the 15%
 * memory reserve and 10% video memory reserve that were already the defaults, with
 * no sustained CPU or graphics guard. A machine nobody has configured therefore
 * reads as Balanced and behaves exactly as it always did. The sustained guards are
 * something the owner opts into by choosing Conservative or by typing a ceiling.
 */

/** The four things the owner can pick. Only `custom` is not a set of numbers. */
export type CapacityProfileKey = 'conservative' | 'balanced' | 'performance' | 'custom';

/** A preset key; `custom` is excluded because it has no values of its own. */
export type CapacityPresetKey = Exclude<CapacityProfileKey, 'custom'>;

/** The dimensions a preset actually sets. Worker seats are deliberately not here. */
export interface CapacityProfileValues {
  /** How much memory must stay free before another Worker starts. */
  readonly reserveMemoryPercent: number;
  /** The same for video memory; ignored by a computer that cannot see its graphics. */
  readonly reserveGraphicsPercent: number;
  /** Sustained processor ceiling for NEW dispatch, or null when that guard is off. */
  readonly cpuCeilingPercent: number | null;
  /** Sustained graphics ceiling for NEW dispatch, or null when that guard is off. */
  readonly graphicsCeilingPercent: number | null;
}

/** Every limit one machine carries, including the seat count a preset never touches. */
export interface MachineCapacityLimits extends CapacityProfileValues {
  /** The hard maximum number of Workers this computer may hold at once. */
  readonly workerSeats: number | null;
}

export interface CapacityProfileDef {
  readonly key: CapacityPresetKey;
  readonly label: string;
  /** One line the owner reads before saving. */
  readonly blurb: string;
  readonly values: CapacityProfileValues;
}

/**
 * The preset table, in the order the owner sees it.
 *
 * These are the only capacity numbers this lane introduces, and they are all
 * here rather than hidden in a component, a migration or a default.
 */
export const CAPACITY_PROFILES: readonly CapacityProfileDef[] = [
  {
    key: 'conservative',
    label: 'Conservative',
    blurb: 'Keeps most of the machine for you, and stops new work when it stays busy.',
    values: {
      reserveMemoryPercent: 30,
      reserveGraphicsPercent: 25,
      cpuCeilingPercent: 70,
      graphicsCeilingPercent: 70,
    },
  },
  {
    key: 'balanced',
    label: 'Balanced',
    blurb: 'What Brunel already did: keep 15% of memory and 10% of video memory free, no load ceiling.',
    values: {
      reserveMemoryPercent: 15,
      reserveGraphicsPercent: 10,
      cpuCeilingPercent: null,
      graphicsCeilingPercent: null,
    },
  },
  {
    key: 'performance',
    label: 'Performance',
    blurb: 'Uses more of the machine: smaller reserves, and no load ceiling.',
    values: {
      reserveMemoryPercent: 8,
      reserveGraphicsPercent: 5,
      cpuCeilingPercent: null,
      graphicsCeilingPercent: null,
    },
  },
] as const;

/** Every choice the owner has, presets first and Custom last. */
export const CAPACITY_PROFILE_ORDER: readonly CapacityProfileKey[] = [
  'conservative', 'balanced', 'performance', 'custom',
] as const;

/** The numbers behind a preset, or null for Custom (which has none of its own). */
export function capacityProfileValues(key: CapacityProfileKey): CapacityProfileValues | null {
  return CAPACITY_PROFILES.find((profile) => profile.key === key)?.values ?? null;
}

/** The owner-facing name of any choice, including Custom. */
export function capacityProfileLabel(key: CapacityProfileKey): string {
  return CAPACITY_PROFILES.find((profile) => profile.key === key)?.label ?? 'Custom';
}

/** The line shown beside the name before anything is saved. */
export function capacityProfileBlurb(key: CapacityProfileKey): string {
  return CAPACITY_PROFILES.find((profile) => profile.key === key)?.blurb
    ?? 'Your own numbers. Brunel never changes these for you.';
}

/** Is this key one of the three presets? */
export function isCapacityPreset(key: string): key is CapacityPresetKey {
  return CAPACITY_PROFILES.some((profile) => profile.key === key);
}

function sameCeiling(a: number | null, b: number | null): boolean {
  return a === null ? b === null : b !== null && a === b;
}

/**
 * Which preset a machine's actual numbers match, or `custom` when they match none.
 *
 * Derived, never stored: this is what makes "never silently change an owner's
 * Custom values" structural rather than a promise. Seats are not compared,
 * because seats are a property of the computer rather than of the owner's
 * appetite for using it.
 */
export function resolveCapacityProfile(limits: CapacityProfileValues): CapacityProfileKey {
  for (const profile of CAPACITY_PROFILES) {
    if (limits.reserveMemoryPercent === profile.values.reserveMemoryPercent
      && limits.reserveGraphicsPercent === profile.values.reserveGraphicsPercent
      && sameCeiling(limits.cpuCeilingPercent, profile.values.cpuCeilingPercent)
      && sameCeiling(limits.graphicsCeilingPercent, profile.values.graphicsCeilingPercent)) {
      return profile.key;
    }
  }
  return 'custom';
}

/** The lowest and highest reserve the guarded write accepts, as the database states them. */
export const RESERVE_PERCENT_MIN = 0;
export const RESERVE_PERCENT_MAX = 90;

/**
 * The lowest and highest sustained ceiling the guarded write accepts. A ceiling
 * under 10% would refuse a machine that is merely awake, so the database will not
 * take one; turning the guard off is `null`, which is a different thing entirely.
 */
export const CEILING_PERCENT_MIN = 10;
export const CEILING_PERCENT_MAX = 100;

/** Zero makes a computer a viewer/control client without Worker capacity. */
export const WORKER_SEATS_MIN = 0;
export const WORKER_SEATS_MAX = 8;

/**
 * The sustained-guard contract, as the database seeds it in
 * `brunel.capacity_guard_contract`. These are defaults for reading and for tests:
 * the row is what actually decides, and the owner can see it.
 */
export interface CapacityGuardContract {
  /** The bounded recent window a sustained reading is averaged over. */
  readonly windowSeconds: number;
  /** How many readings that window must contain before it may hold anything back. */
  readonly minSamples: number;
  /** How far under the ceiling a sustained reading must fall before the guard lets go. */
  readonly releaseMarginPercent: number;
  /** How long a guard must hold its answer before it is allowed to change it again. */
  readonly cooldownSeconds: number;
}

export const DEFAULT_CAPACITY_GUARD_CONTRACT: CapacityGuardContract = {
  windowSeconds: 120,
  minSamples: 2,
  releaseMarginPercent: 8,
  cooldownSeconds: 60,
};

/** What the owner typed. An empty box keeps what the machine already has. */
export interface CapacityLimitsInput {
  readonly memory?: string;
  readonly graphics?: string;
  readonly cpuCeiling?: string;
  readonly graphicsCeiling?: string;
  readonly seats?: string;
}

/** Exactly the keys `public.set_node_limits` accepts beside the machine's identifiers. */
export interface CapacityLimitsFields {
  readonly reserveMemoryPercent?: number;
  readonly reserveGraphicsPercent?: number;
  /** null turns the sustained processor guard off. */
  readonly cpuCeilingPercent?: number | null;
  /** null turns the sustained graphics guard off. */
  readonly graphicsCeilingPercent?: number | null;
  readonly workerSeats?: number;
}

export type CapacityLimitsCheck =
  | { readonly ok: true; readonly value: CapacityLimitsFields }
  | { readonly ok: false; readonly message: string };

/** The word that turns a ceiling off rather than leaving it alone. */
export const CEILING_OFF_WORD = 'off';

function wholePercent(
  text: string, label: string, low: number, high: number,
): { ok: true; value: number } | { ok: false; message: string } {
  if (!/^[0-9]{1,3}$/.test(text)) {
    return { ok: false, message: `${label} must be a whole number of percent between ${low} and ${high}.` };
  }
  const value = Number(text);
  if (value < low || value > high) {
    return { ok: false, message: `${label} must be between ${low} and ${high} percent.` };
  }
  return { ok: true, value };
}

/**
 * What to send, or why nothing can be. A box left empty keeps what the computer
 * already has, `off` clears a ceiling, and no value outside the range the
 * database accepts ever reaches it.
 */
export function checkCapacityLimits(input: CapacityLimitsInput): CapacityLimitsCheck {
  const memory = (input.memory ?? '').trim();
  const graphics = (input.graphics ?? '').trim();
  const cpuCeiling = (input.cpuCeiling ?? '').trim();
  const graphicsCeiling = (input.graphicsCeiling ?? '').trim();
  const seats = (input.seats ?? '').trim();
  if (memory === '' && graphics === '' && cpuCeiling === '' && graphicsCeiling === '' && seats === '') {
    return { ok: false, message: 'Change at least one limit, or leave this machine as it is.' };
  }
  let value: CapacityLimitsFields = {};
  if (memory !== '') {
    const parsed = wholePercent(memory, 'The memory reserve', RESERVE_PERCENT_MIN, RESERVE_PERCENT_MAX);
    if (!parsed.ok) return { ok: false, message: parsed.message };
    value = { ...value, reserveMemoryPercent: parsed.value };
  }
  if (graphics !== '') {
    const parsed = wholePercent(graphics, 'The video memory reserve', RESERVE_PERCENT_MIN, RESERVE_PERCENT_MAX);
    if (!parsed.ok) return { ok: false, message: parsed.message };
    value = { ...value, reserveGraphicsPercent: parsed.value };
  }
  if (cpuCeiling !== '') {
    if (cpuCeiling.toLowerCase() === CEILING_OFF_WORD) {
      value = { ...value, cpuCeilingPercent: null };
    } else {
      const parsed = wholePercent(cpuCeiling, 'The processor ceiling', CEILING_PERCENT_MIN, CEILING_PERCENT_MAX);
      if (!parsed.ok) return { ok: false, message: parsed.message };
      value = { ...value, cpuCeilingPercent: parsed.value };
    }
  }
  if (graphicsCeiling !== '') {
    if (graphicsCeiling.toLowerCase() === CEILING_OFF_WORD) {
      value = { ...value, graphicsCeilingPercent: null };
    } else {
      const parsed = wholePercent(graphicsCeiling, 'The graphics ceiling', CEILING_PERCENT_MIN, CEILING_PERCENT_MAX);
      if (!parsed.ok) return { ok: false, message: parsed.message };
      value = { ...value, graphicsCeilingPercent: parsed.value };
    }
  }
  if (seats !== '') {
    if (!/^[0-9]$/.test(seats) || Number(seats) < WORKER_SEATS_MIN || Number(seats) > WORKER_SEATS_MAX) {
      return { ok: false, message: `Worker seats must be a whole number between ${WORKER_SEATS_MIN} and ${WORKER_SEATS_MAX}.` };
    }
    value = { ...value, workerSeats: Number(seats) };
  }
  return { ok: true, value };
}

/** The exact fields a preset writes, ready for `public.set_node_limits`. */
export function capacityProfileFields(key: CapacityProfileKey): CapacityLimitsFields | null {
  const values = capacityProfileValues(key);
  if (values === null) return null;
  return {
    reserveMemoryPercent: values.reserveMemoryPercent,
    reserveGraphicsPercent: values.reserveGraphicsPercent,
    cpuCeilingPercent: values.cpuCeilingPercent,
    graphicsCeilingPercent: values.graphicsCeilingPercent,
  };
}

/** How a ceiling reads on screen: a percentage, or "off" - never a zero. */
export function ceilingText(percent: number | null): string {
  return percent === null ? 'off' : `${percent}%`;
}

/**
 * The exact numbers a choice would write, in one line, so the owner can read
 * them before saving rather than after.
 */
export function capacityProfilePreview(key: CapacityProfileKey): string | null {
  const values = capacityProfileValues(key);
  if (values === null) return null;
  return [
    `memory reserve ${values.reserveMemoryPercent}%`,
    `video memory reserve ${values.reserveGraphicsPercent}%`,
    `processor ceiling ${ceilingText(values.cpuCeilingPercent)}`,
    `graphics ceiling ${ceilingText(values.graphicsCeilingPercent)}`,
  ].join(' · ');
}
